/**
 * Represents a booking error status in the self-guided tour system.
 * This class provides predefined error statuses and ensures that only one instance
 * of each status is created and reused.
 */
export default class BookingError {
  constructor(name) {
    this.name = name;
  }

  /**
   * Represents the error status when a tour is unavailable.
   * @returns {BookingError} The instance corresponding to the 'TourUnavailable' error status.
   */
  static get TourUnavailable() {
    return this.getInstance('TourUnavailable');
  }

  /**
   * Represents the error status when the user has exceeded the booking limit.
   * @returns {BookingError} The instance corresponding to the 'ExceededLimit' error status.
   */
  static get ExceededLimit() {
    return this.getInstance('ExceededLimit');
  }

  /**
   * Represents the error status when there was an issue creating the door access code.
   * @returns {BookingError} The instance corresponding to the 'AccessCode' error status.
   */
  static get AccessCode() {
    return this.getInstance('AccessCode');
  }

  /**
   * Represents the error status for any other unspecified error.
   * @returns {BookingError} The instance corresponding to the 'Other' error status.
   */
  static get Other() {
    return this.getInstance('Other');
  }

  /**
   * Maps a key to its corresponding BookingError instance.
   * @param {string} key - The key representing the error status.
   * @returns {BookingError} The corresponding BookingError instance.
   */
  static fromKey(key) {
    const statusMap = {
      TourDateNotAvailable: BookingError.TourUnavailable,
      ToursLimitExceeded: BookingError.ExceededLimit,
      AccessCodeGenerationIssue: BookingError.AccessCode,
      UserBlocked: BookingError.Other,
      UserPhoneNumberBlocked: BookingError.Other,
      InvalidTourInformation: BookingError.Other,
      InvalidTourDate: BookingError.Other,
      DefaultError: BookingError.Other,
    };
    return statusMap[key] || BookingError.Other;
  }

  /**
   * Retrieves or creates a singleton instance of a BookingError for the given key.
   * @param {string} key - The key representing the error status.
   * @returns {BookingError} The singleton instance for the given key.
   */
  static getInstance(key) {
    if (!this.instances[key]) {
      this.instances[key] = new BookingError(key);
    }
    return this.instances[key];
  }

  /**
   * Converts the BookingError instance to its string representation.
   * @returns {string} The name of the error status.
   */
  toString() {
    return `${this.name}`;
  }
}

Object.defineProperty(BookingError, 'instances', { value: {} });
